import { HeaderBar } from "./layout-components/header-bar/header-bar.component";
import "./styles.css";
import { Toaster } from "@components/ui/sonner";
import { Loading } from "@/components/src/animations/loading";
import { useEffect, useState } from "react";
import { useCentralStore } from "../store/Central";
import { useRealtimeStore } from "@/lib/store/Realtime";
import { EnterTotp } from "../pages/auth/enter-totp.page";
import { useLocation } from "react-router-dom";
import IntercomSnippet from "../components/intercom";
import { MFADialog } from "../components/mfa/mfa-dialog.component";
import { JobTabs } from "../components/job-tabs/job-tabs.component";
import { PatientTabs } from "../components/patient-tabs/patient-tabs.component";
import { IdInfo } from "./id-info.component";
import { DlSideNavigation } from "./layout-components/app-bar/DlSideNavigation";
import { isDeveloper, isPosthogDeveloper } from "@/lib/utils/is-app-user";
import { HelpPanel } from "@/components/src/help/help-panel.component";
import { useHelpStore } from "@/lib/store/help.store";
import { cn } from "../lib/utils/cn";
import { usePostHog } from "posthog-js/react";
import { ReleaseNotification } from "@components/release-notification/release-notification.component";
import { AuthBanner } from "@/components/src/auth-banner/auth-banner.component";
import { getLocalStorage } from "@/lib/utils/local-storage";
import { RateLimitDialog, ConnectionOverlay } from "@/lib/supabase";
import { PatientMatchingDialog } from "../components/PatientMatchingDialog";

export const Layout: React.FC<{
	children: React.ReactNode;
}> = ({ children }) => {
	const location = useLocation();
	const isClientPage = location.pathname.includes("/client/");
	const isVendorPage = location.pathname.includes("/vendor/");
	const isJobPage =
		(isClientPage || isVendorPage) && location.pathname.includes("/job/");

	const {
		profile,
		loading,
		mfaDialogOpen,
		setMFADialog,
		mfaAuthenticated,
		mfaVerified,
		analyticsEnabled,
		organization,
		appInitializedAt,
	} = useCentralStore((state) => ({
		profile: state.profile,
		loading: !state.initialized,
		mfaDialogOpen: state.mfaDialogOpen,
		setMFADialog: state.setMFADialog,
		mfaVerified: state.mfaVerified,
		mfaAuthenticated: state.mfaAuthenticated,
		analyticsEnabled: state.organization?.is_analytics,
		organization: state.organization,
		appInitializedAt: state.appInitializedAt,
	}));

	const isHelpOpen = useHelpStore((state) => state.isOpen);

	const { subscribe, unsubscribe } = useRealtimeStore((state) => ({
		subscribe: state.subscribe,
		unsubscribe: state.unsubscribe,
	}));

	const postHog = usePostHog();

	useEffect(() => {
		if (!mfaAuthenticated) return;
		subscribe();

		return () => {
			unsubscribe();
		};
	}, [mfaVerified, mfaAuthenticated]);

	useEffect(() => {
		if (!analyticsEnabled) return;

		postHog.opt_in_capturing();
		postHog.identify(profile?.email ?? undefined, {
			is_dev_account: isPosthogDeveloper(import.meta.env, profile?.email),
			organization_id: profile?.organization_id ?? undefined,
			organization_name: organization?.name ?? undefined,
		});

		if (organization?.id) {
			postHog.group("organization", organization.id, {
				name: organization.name,
				type: "lab",
				subscription_tier: organization.subscription_tier,
				created_at: organization.created_at,
				is_onboarded: organization.is_onboarded,
			});

			const isFirstLogin = !postHog.get_property("has_logged_in");
			if (isFirstLogin) {
				postHog.capture("first_login", {
					app: "dentlab",
				});
				postHog.setPersonProperties(undefined, {
					has_logged_in: true,
					first_login_at: new Date().toISOString(),
				});
			}
		}

		return () => {
			postHog.opt_out_capturing();
		};
	}, [analyticsEnabled]);

	if (mfaVerified && !mfaAuthenticated) {
		return <EnterTotp />;
	}

	if (loading || !mfaVerified) {
		return <Loading height="100vh" width="100vw" />;
	}
	return (
		<div className="flex flex-col bg-stone-100 h-screen">
			<div style={{ paddingLeft: "var(--AppBarWidth-COLLAPSED)" }}>
				<AuthBanner />
			</div>

			<div className="flex flex-row flex-1 overflow-hidden">
				<DlSideNavigation className="side-navigation-overlay" />

				<div className="flex flex-col flex-1 overflow-hidden bg-stone-100 main-content-with-sidebar">
					<ReleaseNotification
						appInitializedAt={appInitializedAt}
						app="dentlab"
					/>
					{(window.location.hostname.includes("localhost") ||
						isDeveloper(import.meta.env, profile?.email)) &&
						getLocalStorage("showIdInfo", false) && <IdInfo />}
					<IntercomSnippet />
					<HeaderBar />

					<div className="flex flex-row gap-2">
						<JobTabs />
						<PatientTabs />
					</div>

					{/* Main content area - flex container for job list portal and main content */}
					<div className="flex flex-row flex-1 overflow-hidden">
						{/* Portal target for job list between sidebar and main content */}
						{(isClientPage || isVendorPage) && (
							<div id="job-list-portal" className="h-full" />
						)}

						{/* Main content */}
						<div className="flex-1 overflow-hidden">
							<div className="h-full w-full overflow-auto p-1">
								<div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden h-full overflow-y-auto">
									{children}
								</div>
							</div>
							<Toaster />
						</div>
						{isJobPage && (
							<div id="job-chat-portal" className="h-full" />
						)}
						<div
							className={cn(
								"h-full transition-all duration-300 ease-in-out overflow-hidden",
								isHelpOpen ? "w-[400px]" : "w-0"
							)}
						>
							<div
								className={cn(
									"h-full transition-opacity duration-300 ease-in-out",
									isHelpOpen ? "opacity-100" : "opacity-0"
								)}
							>
								<HelpPanel
									app="dentlab"
									currentRoute={location.pathname}
								/>
							</div>
						</div>
					</div>
				</div>
			</div>

			<MFADialog open={mfaDialogOpen} onOpen={setMFADialog} />
			<RateLimitDialog />
			<ConnectionOverlay />
			<PatientMatchingDialog />
		</div>
	);
};
